import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { getProductImage } from '../utils/productImages';
import api from '../services/api';
import toast from '../utils/toast';
import CharitySelector from '../components/CharitySelector';
import { useHoldTimer } from '../hooks/useHoldTimer';
import { trackInitiateCheckout, trackCompleteRegistration, identifyUser } from '../utils/metaPixel';
import { trackBeginCheckout } from '../utils/ga4';
import './Cart.css';

const Cart = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, register, login } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');
  const [couponMessageType, setCouponMessageType] = useState(''); // 'success' or 'error'
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount_percent }
  const [showAddressModal, setShowAddressModal] = useState(false); // eslint-disable-line no-unused-vars
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [amphiAcknowledged, setAmphiAcknowledged] = useState(false);
  const processingRef = useRef(false);
  const { getItemSeconds, formatTime } = useHoldTimer();
  const [savedCard, setSavedCard] = useState(null);
  const [quickCheckoutLoading, setQuickCheckoutLoading] = useState(false);

  // Pre-checkout auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('register'); // 'register' or 'login'
  const [authFullName, setAuthFullName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authConfirmEmail, setAuthConfirmEmail] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirmPassword, setAuthConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authSmsConsent, setAuthSmsConsent] = useState(false);

  // Post-purchase upsell window (shared with /order-success UpsellBanner):
  //   - upsellWindow  { sessionId, caseKey, expiresAt }  → hide coupon input
  //   - upsellAmpCoupon { sessionId, code, targetRoute, expiresAt }
  //       → auto-apply the UPSELL-AMP-20 coupon AND tag the next checkout
  //         with `upsell_source_session_id` so the new order's success page
  //         will suppress its own upsell banner.
  // Both flags self-expire client-side; the backend also enforces the TTL.
  const [upsellWindowActive, setUpsellWindowActive] = useState(false);
  const [upsellSourceSessionId, setUpsellSourceSessionId] = useState(null);
  const upsellAutoApplyRef = useRef(false);

  useEffect(() => {
    loadCart();
    // If user is logged in, pre-fill their email
    if (isAuthenticated && user?.email) {
      setUserEmail(user.email);
      // Fetch saved card for one-click checkout
      api.getSavedCard().then(res => {
        if (res?.success && res.has_card && !res.card?.is_expired) setSavedCard(res.card);
      }).catch(() => {});
    }

    // Restore cart when user navigates back from Stripe (browser bfcache)
    const handlePageShow = (event) => {
      if (event.persisted) {
        loadCart();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    const handleHoldExpired = () => loadCart();
    window.addEventListener('holdExpired', handleHoldExpired);
    window.addEventListener('cartUpdated', handleHoldExpired);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('holdExpired', handleHoldExpired);
      window.removeEventListener('cartUpdated', handleHoldExpired);
    };
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore persisted coupon on mount (survives navigation between pages).
  // IMPORTANT: only restore if the stored coupon belongs to the current user
  // — otherwise a stale upsell coupon from user A leaks into user B's cart.
  useEffect(() => {
    if (appliedCoupon) return;
    try {
      const saved = localStorage.getItem('appliedCoupon');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.code) {
          const currentEmail = (user?.email || '').trim().toLowerCase();
          const storedEmail = (parsed.forEmail || '').trim().toLowerCase();
          // If the coupon was stamped with a buyer email and it doesn't
          // match the current user, discard the stale entry.
          if (storedEmail && currentEmail && storedEmail !== currentEmail) {
            localStorage.removeItem('appliedCoupon');
            return;
          }
          setAppliedCoupon(parsed);
          setCouponCode(parsed.code);
          setCouponMessage(`Coupon "${parsed.code}" applied! ${parsed.discount_percent}% off`);
          setCouponMessageType('success');
          return;
        }
      }
    } catch { /* ignore parse errors */ }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply referral coupon from ?ref=CODE link (stored in localStorage by App.jsx)
  useEffect(() => {
    if (cartItems.length === 0 || appliedCoupon) return;
    // If the upsell window is active we suppress the coupon UI entirely —
    // don't silently re-apply referral codes during that window either.
    if (upsellWindowActive) return;
    const refCode = localStorage.getItem('referralCoupon');
    if (!refCode) return;
    setCouponCode(refCode);
    // Use the same validation path as manual apply — pass the code directly
    // to avoid stale closure issues with couponCode state.
    (async () => {
      await handleApplyCoupon(refCode);
    })();
  }, [cartItems, upsellWindowActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Post-purchase upsell integration ─────────────────────────────────────
  // Watches the `upsellWindow` localStorage key that /order-success drops when
  // the upsell banner is rendered. While active:
  //   1. The "Have a coupon?" input is hidden entirely from the cart.
  //   2. If the buyer clicked "Add Sat/Sun Seats" on the banner, the
  //      `upsellAmpCoupon` key is present and UPSELL-AMP-20 is auto-applied
  //      to the cart (amphitheater items only).
  //   3. `upsell_source_session_id` is pulled so the next checkout tags the
  //      new order as upsell-derived → its own success page shows no banner.
  // All three states self-resolve once the 20-min TTL expires or the buyer
  // clears the cart.
  useEffect(() => {
    // Upsell localStorage entries are STRICTLY tied to the buyer email that
    // was written on the /order-success page. If the current logged-in user's
    // email does not match the stored `buyerEmail`, we treat the entries as
    // belonging to "someone else on this browser" and ignore them — this is
    // the fix for the cross-user UPSELL-AMP-20 leak that was applying the
    // discount to unrelated users' carts.
    const currentEmail = (user?.email || '').trim().toLowerCase();

    const readUpsellWindow = async () => {
      let active = false;
      let sourceSession = null;

      // Helper: a stored upsell entry is "owned" by the current session only
      // if we're logged in AND the stored buyerEmail matches. We require an
      // authenticated user — if nobody is logged in, we do NOT apply the
      // coupon (guest cart should never auto-apply a user-origin upsell).
      const isMine = (parsed) => {
        const stored = (parsed?.buyerEmail || '').trim().toLowerCase();
        if (!stored) return false;          // no stamp → legacy/invalid → reject
        if (!currentEmail) return false;    // no user logged in → reject
        return stored === currentEmail;
      };

      try {
        const raw = localStorage.getItem('upsellWindow');
        if (raw) {
          const parsed = JSON.parse(raw);
          const stillValid = parsed?.expiresAt && Number(parsed.expiresAt) > Date.now();
          if (stillValid && isMine(parsed)) {
            active = true;
            sourceSession = parsed.sessionId || null;
          } else if (!stillValid) {
            localStorage.removeItem('upsellWindow');
          }
          // If valid but not mine, leave it alone (don't nuke someone else's
          // session-local state) — just don't honor it here.
        }
      } catch { localStorage.removeItem('upsellWindow'); }

      // Collect candidate upsell coupons from BOTH LS entries:
      //   - `upsellAmpCoupon`  (Add Seats flow → Pacific Amphitheater)
      //   - `upsellPassCoupon` (Festival Pass modal → Festival tickets)
      // Both are stamped with `buyerEmail`; we only honor those owned by
      // the currently-logged-in user. Whichever matches the items in the
      // cart wins — if neither matches, we just don't auto-apply.
      const readLsCoupon = (key) => {
        try {
          const raw = localStorage.getItem(key);
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          const stillValid =
            parsed?.expiresAt && Number(parsed.expiresAt) > Date.now() && parsed.code;
          if (stillValid && isMine(parsed)) return parsed;
          if (!stillValid) localStorage.removeItem(key);
          return null;
        } catch {
          localStorage.removeItem(key);
          return null;
        }
      };

      const ampInfo = readLsCoupon('upsellAmpCoupon');
      const passInfo = readLsCoupon('upsellPassCoupon');

      // Promote whichever coupon matches the cart contents to the
      // auto-apply candidate. Fall back to the other if the first doesn't
      // match — e.g. the buyer manually added AMP seats AFTER using the
      // pass upsell flow.
      const hasAmp = cartItems.some((i) => i.type === 'amphitheater');
      const hasFestival = cartItems.some((i) => i.type === 'festival');
      let couponInfo = null;
      let couponKind = null;
      if (ampInfo && hasAmp) { couponInfo = ampInfo; couponKind = 'amp'; }
      else if (passInfo && hasFestival) { couponInfo = passInfo; couponKind = 'pass'; }

      if (couponInfo && !sourceSession && couponInfo.sessionId) {
        sourceSession = couponInfo.sessionId;
      }

      setUpsellWindowActive(active);
      setUpsellSourceSessionId(sourceSession);

      // Auto-apply the upsell coupon once per mount cycle. Gated on:
      //   - the upsell window is active AND owned by the current user
      //   - a matching couponInfo entry exists (buyer actually clicked a
      //     banner/modal button that dropped the LS stamp)
      //   - no coupon is already applied and we haven't auto-applied yet
      //   - the cart contains the product type the coupon targets
      if (
        active &&
        couponInfo &&
        !appliedCoupon &&
        !upsellAutoApplyRef.current
      ) {
        upsellAutoApplyRef.current = true;
        try {
          const res = await api.validateCoupon(couponInfo.code, cartItems);
          if (res?.success) {
            const couponObj = {
              code: res.data.code,
              discount_percent: res.data.discount_percent,
              restricted_to_event_date: res.data.restricted_to_event_date || null,
              product_type: res.data.product_type
                || (couponKind === 'pass' ? 'festival' : 'concert'),
            };
            setAppliedCoupon(couponObj);
            setCouponCode(couponObj.code);
            setCouponMessage(
              couponKind === 'pass'
                ? `Upsell ${couponObj.discount_percent}% off applied to your festival pass.`
                : `Upsell ${couponObj.discount_percent}% off applied to Pacific Amphitheater tickets.`,
            );
            setCouponMessageType('success');
            localStorage.setItem('appliedCoupon', JSON.stringify({
              ...couponObj,
              forEmail: (user?.email || '').trim().toLowerCase(),
            }));
          }
        } catch { /* silent — no blocking error for the upsell auto-apply */ }
      }
    };
    readUpsellWindow();
    // Re-check every 30 seconds so we cleanly drop out of the window state
    // when the TTL ticks over while the buyer is on the cart page.
    const id = setInterval(readUpsellWindow, 30 * 1000);
    return () => clearInterval(id);
  }, [cartItems, appliedCoupon, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCart = () => {
    // Try primary cart first, then fallback to pending checkout cart (restored when user clicks back from Stripe)
    let cart = localStorage.getItem('cart');
    const pendingCart = localStorage.getItem('pendingCheckoutCart');
    
    // If primary cart is empty but we have a pending checkout cart, restore it
    if (!cart && pendingCart) {
      cart = pendingCart;
      localStorage.setItem('cart', pendingCart);
      localStorage.removeItem('pendingCheckoutCart');
      // Update header cart count
      try {
        const restored = JSON.parse(pendingCart);
        window.dispatchEvent(new CustomEvent('cartUpdated', { 
          detail: { items: restored.items || [], total: restored.total || 0 } 
        }));
      } catch (e) { /* ignore */ }
    }
    
    if (cart) {
      try {
        const parsedCart = JSON.parse(cart);
        const items = parsedCart.items || [];
        
        // Validate festival ticket IDs - must be valid UUIDs
        const invalidFestivalTickets = items.filter(item => {
          if (item.type === 'festival' || (!item.type && !item.section)) {
            // Check if ticket_type_id or id is a valid UUID (36 chars with dashes)
            const ticketId = item.ticket_type_id || item.id;
            const isValidUUID = ticketId && ticketId.length >= 32 && ticketId.includes('-');
            return !isValidUUID;
          }
          return false;
        });
        
        if (invalidFestivalTickets.length > 0) {
          // Remove invalid tickets and notify user
          const validItems = items.filter(item => {
            if (item.type === 'festival' || (!item.type && !item.section)) {
              const ticketId = item.ticket_type_id || item.id;
              return ticketId && ticketId.length >= 32 && ticketId.includes('-');
            }
            return true; // Keep amphitheater and vendor items
          });
          
          const newTotal = validItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          localStorage.setItem('cart', JSON.stringify({ items: validItems, total: newTotal }));
          setCartItems(validItems);
          
          // Show warning toast
          toast.error('Some tickets in your cart were outdated and have been removed. Please re-add them from the Tickets page.');
          
          window.dispatchEvent(new CustomEvent('cartUpdated', { 
            detail: { items: validItems, total: newTotal } 
          }));
          return;
        }
        
        setCartItems(items);
      } catch (e) {
        console.error('Error parsing cart:', e);
        setCartItems([]);
      }
    }
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedItems = cartItems.map(item => 
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    
    const newTotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify({
      items: updatedItems,
      total: newTotal
    }));
    
    window.dispatchEvent(new CustomEvent('cartUpdated', { 
      detail: { items: updatedItems, total: newTotal } 
    }));
  };

  const removeItem = (itemId) => {
    // Find the item to check if it's a box seat
    const itemToRemove = cartItems.find(item => item.id === itemId);
    
    // If it's a box seat, remove ALL seats from that box (sold as package)
    let updatedItems;
    let removedItems = [];
    if (itemToRemove && itemToRemove.type === 'amphitheater' && itemToRemove.section?.toLowerCase().includes('box')) {
      // Get the box name (e.g., "Box Seat 1") and remove all seats from that box
      const boxName = itemToRemove.section;
      removedItems = cartItems.filter(item => item.section === boxName);
      updatedItems = cartItems.filter(item => item.section !== boxName);
    } else {
      if (itemToRemove) removedItems = [itemToRemove];
      updatedItems = cartItems.filter(item => item.id !== itemId);
    }
    
    // Release any backend seat holds (safety net for legacy cart items)
    removedItems.forEach(item => {
      if (item?.type === 'amphitheater' && item?.holdIds) {
        item.holdIds.forEach(holdId => {
          api.releaseSeatHold(holdId).catch(() => {});
        });
      }
    });
    
    const newTotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify({
      items: updatedItems,
      total: newTotal
    }));
    
    window.dispatchEvent(new CustomEvent('cartUpdated', { 
      detail: { items: updatedItems, total: newTotal } 
    }));
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Check if a single cart item matches the applied coupon's product_type
  const itemMatchesCoupon = (item) => {
    if (!appliedCoupon) return false;
    const pt = appliedCoupon.product_type;
    if (pt === 'all') return true;
    const itemType = item.type || 'general';
    if (pt === 'all_tickets' && (itemType === 'festival' || itemType === 'amphitheater')) return true;
    if (pt === 'any_vendor' && itemType === 'vendor-booth') return true;
    if (pt === 'festival' && itemType === 'festival') return true;
    if (pt === 'concert' && itemType === 'amphitheater') {
      // Date-restricted AMP coupons only match the specified event date
      if (appliedCoupon.restricted_to_event_date && item.eventDate) {
        return item.eventDate === appliedCoupon.restricted_to_event_date;
      }
      return true;
    }
    if (itemType === 'vendor-booth') {
      const bt = item.booth_type || '';
      const biz = item.business_type || '';
      const sz = item.booth_size || '10x10';
      if (pt === 'food_truck' && (bt === 'food-truck' || bt.includes('food-truck'))) return true;
      if (pt === 'food_10x10' && biz === 'food' && sz === '10x10') return true;
      if (pt === 'food_20x10' && biz === 'food' && sz === '20x10') return true;
      if (pt === 'food_20x20' && biz === 'food' && sz === '20x20') return true;
      if (pt === 'bazaar_10x10' && biz === 'bazaar' && sz === '10x10') return true;
      if (pt === 'bazaar_20x10' && biz === 'bazaar' && sz === '20x10') return true;
      if (pt === 'bazaar_20x20' && biz === 'bazaar' && sz === '20x20') return true;
    }
    return false;
  };

  // Calculate subtotal of only items matching the coupon's product_type
  const calculateMatchingSubtotal = () => {
    if (!appliedCoupon || appliedCoupon.product_type === 'all') return calculateSubtotal();
    return cartItems.reduce((sum, item) => sum + (itemMatchesCoupon(item) ? item.price * item.quantity : 0), 0);
  };

  const handleApplyCoupon = async (codeOverride) => {
    const code = (typeof codeOverride === 'string' && codeOverride ? codeOverride : couponCode || '').trim().toUpperCase();
    if (!code) {
      setCouponMessage('Please enter a coupon code');
      setCouponMessageType('error');
      return;
    }

    try {
      // Send cart items so backend can validate coupon product_type restriction
      const itemsForValidation = cartItems.map(item => ({
        type: item.type || 'general',
        business_type: item.business_type || '',
        booth_size: item.booth_size || '',
        booth_type: item.booth_type || '',
        eventDate: item.eventDate || '',
      }));
      const response = await api.validateCoupon(code, itemsForValidation);
      
      if (response.success && response.data?.is_valid) {
        const couponObj = {
          code: response.data.code,
          discount_percent: response.data.discount_percent,
          product_type: response.data.product_type || 'all',
          restricted_to_event_date: response.data.restricted_to_event_date || null,
        };
        setAppliedCoupon(couponObj);
        setCouponCode(response.data.code);
        localStorage.setItem('appliedCoupon', JSON.stringify({
          ...couponObj,
          forEmail: (user?.email || '').trim().toLowerCase(),
        }));
        const ptDisplay = response.data.product_type_display;
        const suffix = ptDisplay && response.data.product_type !== 'all' ? ` (applies to ${ptDisplay} only)` : '';
        const fromRef = codeOverride ? ' auto-applied from referral link!' : ' applied!';
        setCouponMessage(`Coupon "${response.data.code}"${fromRef} ${response.data.discount_percent}% off${suffix}`);
        setCouponMessageType('success');
      } else {
        // Handle error response - error can be string or object
        const errorMsg = typeof response.error === 'string' 
          ? response.error 
          : (response.error?.message || 'Invalid coupon code');
        setCouponMessage(errorMsg);
        setCouponMessageType('error');
        setAppliedCoupon(null);
      }
    } catch (err) {
      console.error('Coupon validation error:', err);
      // Handle API errors (404, 400, etc.)
      const errorMsg = err.error || err.message || 'Invalid coupon code';
      setCouponMessage(errorMsg);
      setCouponMessageType('error');
      setAppliedCoupon(null);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponMessage('');
    setCouponMessageType('');
    localStorage.removeItem('appliedCoupon');
    localStorage.removeItem('referralCoupon');
  };

  // handleUpdateCart removed — cart updates in real-time with React

  const handleChangeAddress = () => { // eslint-disable-line no-unused-vars
    setShowAddressModal(true);
  };

  const closeAddressModal = () => { // eslint-disable-line no-unused-vars
    setShowAddressModal(false);
  };

  const handleProceedToCheckout = () => {
    // CRITICAL: Prevent double-clicks with ref (survives re-renders)
    if (processingRef.current || isProcessing) return;

    // Validate festival ticket acknowledgment
    const hasFestivalTickets = cartItems.some(i => i.type === 'festival');
    if (hasFestivalTickets && !amphiAcknowledged) {
      setEmailError('Please acknowledge that festival tickets do not include amphitheater access.');
      return;
    }

    // Validate charity selection is made (REQUIRED)
    if (!selectedCharity) {
      setEmailError('Please select where to donate 5% of your purchase before proceeding.');
      document.getElementById('charity-selector-cart')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // If user is already authenticated, proceed directly to Stripe
    if (isAuthenticated && user?.email) {
      proceedToStripeCheckout(user.email, user.full_name || '', user.phone || '');
      return;
    }

    // Not authenticated - show pre-checkout auth modal
    setShowAuthModal(true);
    setAuthError('');
    setAuthMode('register');
    // Pre-fill from vendor booth items if available
    const vendorItem = cartItems.find(item => item.type === 'vendor-booth');
    if (vendorItem) {
      if (vendorItem.vendor_name) setAuthFullName(vendorItem.vendor_name);
      else if (vendorItem.legal_name) setAuthFullName(vendorItem.legal_name);
      if (vendorItem.vendor_email) setAuthEmail(vendorItem.vendor_email);
      if (vendorItem.vendor_phone) setAuthPhone(vendorItem.vendor_phone);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    if (authLoading) return;
    setAuthError('');

    if (authMode === 'register') {
      // Validate all required fields
      if (!authFullName.trim()) { setAuthError('Full name is required'); return; }
      if (!authEmail.trim()) { setAuthError('Email is required'); return; }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(authEmail)) { setAuthError('Please enter a valid email address'); return; }
      if (!authConfirmEmail.trim()) { setAuthError('Please confirm your email address'); return; }
      if (!emailRegex.test(authConfirmEmail)) { setAuthError('Please enter a valid confirmation email address'); return; }
      if (authEmail.trim().toLowerCase() !== authConfirmEmail.trim().toLowerCase()) { setAuthError('Emails do not match'); return; }
      if (!authPassword) { setAuthError('Password is required'); return; }
      if (authPassword.length < 6) { setAuthError('Password must be at least 6 characters'); return; }
      if (authPassword !== authConfirmPassword) { setAuthError('Passwords do not match'); return; }

      setAuthLoading(true);
      try {
        const result = await register(authEmail, authPassword, authConfirmPassword, authFullName, authPhone, authSmsConsent, authConfirmEmail);
        if (result?.success) {
          // Registration successful - user is now logged in via AuthContext
          setShowAuthModal(false);
          toast.success('Account created! Proceeding to payment...');
          // ── Meta Pixel: CompleteRegistration ──
          trackCompleteRegistration({
            status: 'complete',
            userData: { email: authEmail, phone: authPhone, firstName: authFullName.split(' ')[0], lastName: authFullName.split(' ').slice(1).join(' ') },
          });
          // Proceed to Stripe with registered user details
          proceedToStripeCheckout(authEmail, authFullName, authPhone);
        } else {
          const errMsg = result?.error?.message || result?.error || 'Registration failed';
          setAuthError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
        }
      } catch (err) {
        const errData = err?.response?.data;
        const errMsg = errData?.email?.[0] || errData?.error?.message || errData?.error || err.message || 'Registration failed';
        // If email already exists, switch to login mode
        if (typeof errMsg === 'string' && (errMsg.toLowerCase().includes('already exists') || errMsg.toLowerCase().includes('already registered'))) {
          setAuthMode('login');
          setAuthError('An account with this email already exists. Please log in.');
        } else {
          setAuthError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
        }
      } finally {
        setAuthLoading(false);
      }
    } else {
      // Login mode
      if (!authEmail.trim()) { setAuthError('Email is required'); return; }
      if (!authPassword) { setAuthError('Password is required'); return; }

      setAuthLoading(true);
      try {
        const result = await login(authEmail, authPassword);
        if (result?.success) {
          setShowAuthModal(false);
          toast.success('Logged in! Proceeding to payment...');
          const loggedInUser = result.data?.user;
          proceedToStripeCheckout(
            loggedInUser?.email || authEmail,
            loggedInUser?.full_name || authFullName,
            loggedInUser?.phone || authPhone
          );
        } else {
          setAuthError(result?.error?.message || result?.error || 'Invalid email or password');
        }
      } catch (err) {
        setAuthError(err?.response?.data?.non_field_errors?.[0] || err.message || 'Invalid email or password');
      } finally {
        setAuthLoading(false);
      }
    }
  };

  const proceedToStripeCheckout = async (email, fullName = '', phone = '') => {
    // Double-click guard with ref
    if (processingRef.current) return;
    processingRef.current = true;
    setIsProcessing(true);
    setEmailError('');
    
    try {
      // Prepare cart items for checkout
      const checkoutItems = cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        type: item.type || 'general',
        section: item.section,
        row: item.row,
        seat: item.seat,
        day: item.day,
        holdIds: item.holdIds,
        seatDetails: item.seatDetails,
        eventDate: item.eventDate,
        ticket_type_id: item.ticket_type_id,
        business_type: item.business_type,
        business_name: item.business_name,
        booth_size: item.booth_size,
        booth_type: item.booth_type,
        days: item.days,
        halal_certified: item.halal_certified,
        vendor_email: item.vendor_email,
        vendor_phone: item.vendor_phone,
        vendor_name: item.vendor_name,
        vendor_first_name: item.vendor_first_name,
        vendor_last_name: item.vendor_last_name,
        legal_name: item.legal_name,
        instagram: item.instagram,
        facebook: item.facebook,
        tiktok: item.tiktok,
        business_subtype: item.business_subtype,
        same_as_legal: item.same_as_legal,
        health_permit: item.health_permit,
        handwashing_station: item.handwashing_station,
        oc_health_presentation: item.oc_health_presentation,
        bds_compliance: item.bds_compliance,
        right_of_refusal: item.right_of_refusal,
        charity_selection: item.charity_selection,
        setup_terms: item.setup_terms,
        license_plate_number: item.license_plate_number,
        product_type: item.product_type
      }));

      // Parse name for form data - use email prefix if no name provided
      let firstName, lastName;
      if (fullName && fullName.trim()) {
        const nameParts = fullName.split(' ');
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ') || '';
      } else {
        // Extract name from email (before @)
        const emailPrefix = email.split('@')[0] || '';
        // Capitalize and clean up: "john.doe" -> "John", "Doe"
        const parts = emailPrefix.replace(/[._-]/g, ' ').split(' ').filter(Boolean);
        firstName = parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1) : '';
        lastName = parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') || '';
      }

      // Get phone from: argument > user profile > vendor booth items
      let checkoutPhone = phone || user?.phone || '';
      if (!checkoutPhone) {
        // Try to get phone from vendor booth items
        const vendorItem = checkoutItems.find(item => item.vendor_phone);
        if (vendorItem) {
          checkoutPhone = vendorItem.vendor_phone;
        }
      }
      
      // Form data for checkout
      const formData = {
        email: email,
        firstName: firstName,
        lastName: lastName,
        phone: checkoutPhone
      };
      
      // Stable idempotency key based on cart contents (same cart = same key, prevents duplicate orders)
      const cartHash = checkoutItems.map(i => `${i.ticket_type_id || i.id}_${i.quantity}_${i.price}`).sort().join('|');
      const idempotencyKey = `cart_${btoa(cartHash).slice(0, 40)}_${email.slice(0, 10)}`;

      // Only use coupon if explicitly applied in THIS session
      const couponData = appliedCoupon;

      // Store charity selection in localStorage so success page can access it
      const charityForBackend = selectedCharity ? {
        country: selectedCharity.country,
        category: selectedCharity.category,
        organizations: selectedCharity.organizations?.map(o => o.name) || []
      } : null;
      if (charityForBackend) {
        localStorage.setItem('charity_selection', JSON.stringify(charityForBackend));
      }

      // ── Meta Pixel: InitiateCheckout ──
      const checkoutTotal = checkoutItems.reduce((sum, i) => sum + (i.price * (i.quantity || 1)), 0);
      identifyUser({ email, firstName, lastName: fullName ? fullName.split(' ').slice(1).join(' ') : '', phone });
      trackInitiateCheckout({
        value: checkoutTotal,
        currency: 'USD',
        numItems: checkoutItems.reduce((sum, i) => sum + (i.quantity || 1), 0),
        contentIds: checkoutItems.map(i => i.id || i.ticket_type_id),
        userData: { email, firstName, lastName: fullName ? fullName.split(' ').slice(1).join(' ') : '', phone },
      });

      // ── GA4: begin_checkout ──
      trackBeginCheckout({
        value: checkoutTotal,
        currency: 'USD',
        items: checkoutItems.map(i => ({
          id: i.id || i.ticket_type_id,
          name: i.name || i.ticket_type_name,
          category: i.category || 'Ticket',
          price: i.price,
          quantity: i.quantity || 1,
        })),
        purchaseType: checkoutItems.some(i => (i.name || '').toLowerCase().includes('vendor')) ? 'vendor_booth' : 'ticket',
      });

      // Create checkout session - backend will handle user creation
      const extraMeta = {
        ...(amphiAcknowledged ? { festival_disclaimer_accepted: true } : {}),
        // If this checkout was launched from a post-purchase upsell banner,
        // tag it so the resulting order's success page suppresses its own
        // upsell banner. The backend enforces this in `determine_case`.
        ...(upsellSourceSessionId ? { upsell_source_session_id: upsellSourceSessionId } : {}),
      };
      const extraMetaArg = Object.keys(extraMeta).length ? extraMeta : null;
      const response = await api.createCheckoutSession(formData, checkoutItems, idempotencyKey, couponData, charityForBackend, extraMetaArg);

      if (response.success && response.data.session_url) {
        // Save cart as backup so user can restore if they click back from Stripe
        const currentCart = localStorage.getItem('cart');
        if (currentCart) {
          localStorage.setItem('pendingCheckoutCart', currentCart);
        }
        // Don't clear cart or dispatch empty cartUpdated here — keep badge visible
        // Cart will be cleared on successful order (order-success page)
        // Clear persisted coupon — it's been submitted with this checkout
        localStorage.removeItem('appliedCoupon');
        localStorage.removeItem('referralCoupon');
        // Also clear the upsell auto-apply flags — the discount has been
        // committed into the Stripe session. The sessionStorage gate on
        // /order-success will prevent another banner from showing.
        localStorage.removeItem('upsellAmpCoupon');
        localStorage.removeItem('upsellPassCoupon');
        localStorage.removeItem('upsellWindow');
        // Let the upsell analytics backend record the checkout redirect.
        if (upsellSourceSessionId) {
          api.trackUpsellAction(upsellSourceSessionId, 'checkout_redirect', {
            idempotency_key: idempotencyKey,
          }).catch(() => {});
        }
        // Redirect directly to Stripe Checkout
        window.location.href = response.data.session_url;
      } else {
        throw new Error(response.error?.message || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMsg = error?.response?.data?.error?.message || error?.message || 'Failed to proceed to checkout. Please try again.';
      setEmailError(errorMsg);
      toast.error(errorMsg);
      processingRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleEmailSubmit = async () => {
    // Prevent double-clicks
    if (processingRef.current || isProcessing) return;

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userEmail || !emailRegex.test(userEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Use the shared checkout function
    await proceedToStripeCheckout(userEmail);
  };

  if (cartItems.length === 0) {
    return (
      <div className="page-wrapper">
        <AnnouncementBar />
        
        <section className="hero-section">
          <div className="hero-background-wrapper">
            <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
            <div className="hero-gradient-overlay"></div>
          </div>

          <TornPaperWrapper>
            <div className="cart-page">
              <h1 className="cart-title">Cart</h1>
              <div className="empty-cart">
                <p>Your cart is currently empty.</p>
                <button className="return-to-shop-btn" onClick={() => navigate('/tickets')}>
                  Return to shop
                </button>
              </div>
            </div>
          </TornPaperWrapper>
        </section>

        <Footer />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <AnnouncementBar />
      
      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <div className="cart-page">
            <h1 className="cart-title">Cart</h1>
            
            <div className="cart-table-wrapper">
              <table className="cart-table">
                <thead>
                  <tr>
                    <th className="remove-col"></th>
                    <th className="image-col"></th>
                    <th className="product-col">Product</th>
                    <th className="price-col">Price</th>
                    <th className="quantity-col">Quantity</th>
                    <th className="subtotal-col">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item) => (
                    <tr key={item.id}>
                      <td className="remove-col">
                        <button 
                          className="remove-item-btn"
                          onClick={() => removeItem(item.id)}
                          aria-label="Remove item"
                        >
                          ×
                        </button>
                      </td>
                      <td className="image-col">
                        <img loading="lazy" src={getProductImage(item)} alt={item.name} className="cart-item-image" />
                      </td>
                      <td className="product-col">
                        {item.name}
                        {/* Show seat details for amphitheater tickets */}
                        {(item.type === 'amphitheater' || item.section) && (
                          <div className="seat-details" style={{fontSize: '0.85em', color: '#666', marginTop: '4px'}}>
                            {item.section && <span>Section: {item.section}</span>}
                            {item.seatDetails && item.seatDetails.length > 0 && (
                              <>
                                {item.seatDetails.length === 1 ? (
                                  <>
                                    {item.seatDetails[0].row && <span> | Row: {item.seatDetails[0].row}</span>}
                                    {item.seatDetails[0].number && <span> | Seat: {item.seatDetails[0].number}</span>}
                                  </>
                                ) : (
                                  <span> | {item.seatDetails.length} seats</span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {(() => { const secs = item.type === 'amphitheater' ? getItemSeconds(item.id) : null; return secs !== null && secs > 0 ? (
                          <div style={{
                            fontSize: '0.85em', fontWeight: '700', marginTop: '6px',
                            color: secs < 60 ? '#ef4444' : '#f59e0b',
                            display: 'flex', alignItems: 'center', gap: '6px'
                          }}>
                            <span style={{fontSize: '1.1em'}}>⏱</span>
                            Hold expires in {formatTime(secs)}
                          </div>
                        ) : secs === 0 ? (
                          <div style={{fontSize: '0.85em', fontWeight: '700', marginTop: '6px', color: '#ef4444'}}>
                            ⚠ Hold expired — seats released
                          </div>
                        ) : null; })()}
                        {/* Show vendor booth options */}
                        {item.type === 'vendor-booth' && (
                          <div className="vendor-options" style={{fontSize: '0.85em', marginTop: '8px'}}>
                            {item.days && (
                              <div style={{display: 'flex', alignItems: 'center', marginBottom: '4px'}}>
                                <span style={{color: '#22c55e', marginRight: '6px', fontSize: '1.1em'}}>✓</span>
                                <span style={{color: '#666'}}>
                                  {item.days === '3days' ? 'Fri-Sun (3 days)' : 'Sat-Sun (2 days)'}
                                </span>
                              </div>
                            )}
                            {item.booth_size && item.booth_size !== '10x10' && (
                              <div style={{display: 'flex', alignItems: 'center', marginBottom: '4px'}}>
                                <span style={{color: '#22c55e', marginRight: '6px', fontSize: '1.1em'}}>✓</span>
                                <span style={{color: '#666'}}>Upgraded to {item.booth_size}</span>
                              </div>
                            )}
                            {item.halal_certified && (
                              <div style={{display: 'flex', alignItems: 'center', marginBottom: '4px'}}>
                                <span style={{color: '#22c55e', marginRight: '6px', fontSize: '1.1em'}}>✓</span>
                                <span style={{color: '#666'}}>Halal Certified</span>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="price-col">
                        {appliedCoupon && itemMatchesCoupon(item) ? (
                          <>
                            <span style={{textDecoration: 'line-through', color: '#999', fontSize: '0.85em'}}>${Number(item.price).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            <br/>
                            <span style={{color: '#28a745', fontWeight: 600}}>${(item.price * (1 - appliedCoupon.discount_percent / 100)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </>
                        ) : (
                          `$${Number(item.price).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                        )}
                      </td>
                      <td className="quantity-col">
                        {/* Disable quantity for amphitheater - each seat is unique */}
                        {/* Disable quantity for vendor booths - only 1 booth per order */}
                        {item.type === 'amphitheater' || item.sectionId ? (
                          <span className="quantity-fixed" title="Amphitheater seats cannot be adjusted">
                            {item.quantity}
                          </span>
                        ) : item.type === 'vendor-booth' || item.type === 'vendor' || item.type === 'vendor-attendee' ? (
                          <span className="quantity-fixed" title="Vendor booth quantity cannot be adjusted">
                            {item.quantity}
                          </span>
                        ) : (
                          <input 
                            type="number" 
                            min="1" 
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                            className="quantity-input"
                          />
                        )}
                      </td>
                      <td className="subtotal-col">
                        {appliedCoupon && itemMatchesCoupon(item) ? (
                          <>
                            <span style={{textDecoration: 'line-through', color: '#999', fontSize: '0.85em'}}>${(item.price * item.quantity).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            <br/>
                            <span style={{color: '#28a745', fontWeight: 600}}>${(item.price * item.quantity * (1 - appliedCoupon.discount_percent / 100)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </>
                        ) : (
                          `$${(item.price * item.quantity).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="cart-actions">
              <div className="coupon-section" style={appliedCoupon ? { maxWidth: 'none' } : {}}>
                {upsellWindowActive ? (
                  // During the 20-min post-purchase upsell window, NO coupon
                  // input is shown at all. An auto-applied upsell discount
                  // (UPSELL-AMP-20) may still be visible via the success
                  // message below when the buyer chose "Add Seats".
                  appliedCoupon ? (
                    <div className="applied-coupon" style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '300px', flexWrap: 'wrap' }}>
                      <span style={{ color: '#333', fontSize: '0.9rem', fontWeight: '500' }}>
                        ✓ {appliedCoupon.code} &nbsp;({appliedCoupon.discount_percent}% off)
                      </span>
                      {couponMessage && couponMessageType === 'success' && (
                        <span style={{ padding: '6px 16px', borderRadius: '4px', fontSize: '0.85rem', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', whiteSpace: 'nowrap' }}>
                          {couponMessage}
                        </span>
                      )}
                    </div>
                  ) : null
                ) : appliedCoupon ? (
                  <div className="applied-coupon" style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '300px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#333', fontSize: '0.9rem', fontWeight: '500' }}>
                      ✓ {appliedCoupon.code}
                    </span>
                    <button onClick={removeCoupon} style={{ padding: '4px 12px', fontSize: '0.8rem', backgroundColor: '#f3f4f6', color: '#dc2626', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>Remove</button>
                    {couponMessage && couponMessageType === 'success' && (
                      <span style={{ padding: '6px 16px', borderRadius: '4px', fontSize: '0.85rem', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', whiteSpace: 'nowrap' }}>
                        {couponMessage}
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <input 
                      type="text" 
                      placeholder="Coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="coupon-input"
                      style={{ color: '#333' }}
                    />
                    <button className="apply-coupon-btn" onClick={handleApplyCoupon}>Apply coupon</button>
                  </>
                )}
                {couponMessage && couponMessageType === 'error' && (
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    marginTop: '0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    border: '1px solid #f5c6cb'
                  }}>
                    {couponMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="cart-totals-charity-row">
              {/* Charity Donation Section - REQUIRED (appears first on mobile/tablet via CSS order) */}
              <div id="charity-selector-cart" className="charity-selector-col">
                <CharitySelector
                  onSelect={(val) => { setSelectedCharity(val); setEmailError(''); }}
                  value={selectedCharity}
                  required={true}
                />
              </div>

              {/* Cart Totals */}
              <div className="cart-totals-section cart-totals-col">
                <h2 className="cart-totals-title">Cart totals</h2>
                
                <div className="cart-totals-table">
                  <div className="totals-row">
                    <span className="totals-label">Subtotal</span>
                    <span className="totals-value">${calculateSubtotal().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  
                  {appliedCoupon && (
                    <div className="totals-row discount-row">
                      <span className="totals-label" style={{color: '#28a745'}}>
                        Discount ({appliedCoupon.discount_percent}% off{appliedCoupon.product_type !== 'all' ? ' —  coupon items only' : ''})
                      </span>
                      <span className="totals-value" style={{color: '#28a745'}}>
                        -${(calculateMatchingSubtotal() * appliedCoupon.discount_percent / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>
                  )}
                  
                  
                  <div className="totals-row">
                    <span className="totals-label">Sales Tax (7.75%)</span>
                    <span className="totals-value">
                      ${(() => {
                        const isVendorOnly = cartItems.length > 0 && cartItems.every(i => i.type === 'vendor-booth');
                        if (isVendorOnly) return '0.00';
                        const sub = appliedCoupon 
                          ? calculateSubtotal() - (calculateMatchingSubtotal() * appliedCoupon.discount_percent / 100)
                          : calculateSubtotal();
                        return (sub * 0.0775).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                      })()}
                    </span>
                  </div>
                  
                  <div className="totals-row total-row">
                    <span className="totals-label">Total</span>
                    <span className="totals-value total-amount">
                      ${(() => {
                        const isVendorOnly = cartItems.length > 0 && cartItems.every(i => i.type === 'vendor-booth');
                        const sub = appliedCoupon 
                          ? calculateSubtotal() - (calculateMatchingSubtotal() * appliedCoupon.discount_percent / 100)
                          : calculateSubtotal();
                        return (sub * (isVendorOnly ? 1 : 1.0775)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                      })()}
                    </span>
                  </div>
                </div>

                {cartItems.some(i => i.type === 'festival') && (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', margin: '0 auto 16px', maxWidth: '520px', textAlign: 'left' }}>
                    <input
                      type="checkbox"
                      checked={amphiAcknowledged}
                      onChange={(e) => setAmphiAcknowledged(e.target.checked)}
                      style={{ marginTop: '3px', accentColor: '#D42533', width: '18px', height: '18px', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#4b5563', lineHeight: 1.5 }}>
                      I understand I am purchasing festival tickets that do not include access to Pacific Amphitheater performances.
                    </span>
                  </label>
                )}

                {isAuthenticated && savedCard && (
                  <button
                    className={`proceed-checkout-btn${!selectedCharity ? ' btn-disabled' : ''}${cartItems.some(i => i.type === 'festival') && !amphiAcknowledged ? ' btn-disabled' : ''}`}
                    onClick={async () => {
                      if (quickCheckoutLoading || processingRef.current) return;
                      const hasFestivalTickets = cartItems.some(i => i.type === 'festival');
                      if (hasFestivalTickets && !amphiAcknowledged) {
                        setEmailError('Please acknowledge that festival tickets do not include amphitheater access.');
                        return;
                      }
                      if (!selectedCharity) {
                        setEmailError('Please select where to donate 5% of your purchase before proceeding.');
                        return;
                      }
                      setQuickCheckoutLoading(true);
                      processingRef.current = true;
                      try {
                        const items = cartItems.map(item => ({
                          ticket_type_id: item.ticket_type_id || item.id,
                          slug: item.slug || item.id,
                          quantity: item.quantity || 1,
                          type: item.type,
                          price: item.price,
                          name: item.name,
                          holdIds: item.holdIds,
                          seatDetails: item.seatDetails,
                          eventDate: item.eventDate,
                          ...(item.metadata || {}),
                        }));
                        const idempotencyKey = `quick_${Date.now()}_${Math.random().toString(36).slice(2)}`;
                        const couponPayload = appliedCoupon ? { code: appliedCoupon.code } : null;
                        const quickExtra = upsellSourceSessionId
                          ? { upsell_source_session_id: upsellSourceSessionId }
                          : null;
                        const quickCharitySelection = selectedCharity ? {
                          country: selectedCharity.country,
                          category: selectedCharity.category,
                          organizations: selectedCharity.organizations?.map(o => o.name) || []
                        } : null;
                        const res = await api.quickCheckout(items, idempotencyKey, couponPayload, quickCharitySelection, quickExtra);
                        if (res?.success) {
                          localStorage.removeItem('cart');
                          localStorage.removeItem('appliedCoupon');
                          localStorage.removeItem('referralCoupon');
                          localStorage.removeItem('upsellAmpCoupon');
                          localStorage.removeItem('upsellWindow');
                          if (upsellSourceSessionId) {
                            api.trackUpsellAction(upsellSourceSessionId, 'checkout_redirect', {
                              quick_checkout: true,
                              idempotency_key: idempotencyKey,
                            }).catch(() => {});
                          }
                          toast.success('Payment successful!');
                          navigate(`/order-success?session_id=${res.payment_intent_id}&order_id=${res.order_id}&order_number=${res.order_number}`);
                        } else if (res?.redirect_to_checkout) {
                          toast.info(res.message || 'Please complete checkout normally.');
                          handleProceedToCheckout();
                        } else {
                          toast.error(res?.message || 'Payment failed');
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.message || err?.message || 'Payment failed. Please try regular checkout.');
                      } finally {
                        setQuickCheckoutLoading(false);
                        processingRef.current = false;
                      }
                    }}
                    disabled={quickCheckoutLoading || !selectedCharity || (cartItems.some(i => i.type === 'festival') && !amphiAcknowledged)}
                    style={{ marginBottom: '10px', backgroundColor: '#16a34a', borderColor: '#16a34a' }}
                  >
                    {quickCheckoutLoading ? 'Processing...' : `Pay with ${savedCard.brand?.charAt(0).toUpperCase()}${savedCard.brand?.slice(1) || 'Card'} ····${savedCard.last4}`}
                  </button>
                )}

                <button 
                  className={`proceed-checkout-btn${!selectedCharity ? ' btn-disabled' : ''}${cartItems.some(i => i.type === 'festival') && !amphiAcknowledged ? ' btn-disabled' : ''}`}
                  onClick={handleProceedToCheckout}
                  disabled={isProcessing || !selectedCharity || (cartItems.some(i => i.type === 'festival') && !amphiAcknowledged)}
                >
                  {isProcessing ? 'Processing...' : 'Proceed to checkout'}
                </button>
                {!selectedCharity && (
                  <p style={{ color: '#D42533', fontWeight: '600', fontSize: '13px', textAlign: 'center', margin: '8px 0 0' }}>
                    Choose your charity to complete your purchase
                  </p>
                )}

                {(() => {
                  const hasTicket = cartItems.some(i => i.type === 'festival' || i.type === 'amphitheater' || i.type === 'general');
                  const hasVendor = cartItems.some(i => i.type === 'vendor-booth' || i.type === 'vendor' || i.type === 'vendor-attendee');
                  return (
                    <div style={{ marginTop: '10px', textAlign: 'center' }}>
                      {hasTicket && (
                        <p style={{ color: '#000', fontWeight: '600', fontSize: '13px', margin: '4px 0' }}>
                          All ticket sales are final. No Refunds.
                        </p>
                      )}
                      {hasVendor && (
                        <p style={{ color: '#000', fontWeight: '600', fontSize: '13px', margin: '4px 0' }}>
                          All vendor sales are final once your vendor application has been approved. No Refunds once approved.
                        </p>
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>

          </div>
        </TornPaperWrapper>
      </section>

      {/* Pre-checkout Auth Modal - OUTSIDE TornPaperWrapper to escape z-index */}
      {showAuthModal && (
        <div className="address-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="address-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px', width: '90%' }}>
            <div className="address-modal-header">
              <h3>{authMode === 'register' ? 'Create Your Account' : 'Welcome Back'}</h3>
              <button className="modal-close-btn" onClick={() => setShowAuthModal(false)}>×</button>
            </div>
            <form onSubmit={handleAuthSubmit} style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              <div className="address-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '14px', color: '#555' }}>
                  {authMode === 'register'
                    ? 'Create an account to proceed to payment. Your tickets will be linked to this account.'
                    : 'Log in to your existing account to proceed.'}
                </p>

                {authMode === 'register' && (
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={authFullName}
                    onChange={(e) => setAuthFullName(e.target.value)}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
                  />
                )}

                <input
                  type="email"
                  placeholder="Email Address *"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
                />

                {authMode === 'register' && (
                  <input
                    type="email"
                    placeholder="Confirm Email Address *"
                    value={authConfirmEmail}
                    onChange={(e) => setAuthConfirmEmail(e.target.value)}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
                  />
                )}

                {authMode === 'register' && (
                  <input
                    type="tel"
                    placeholder="Phone Number (Optional)"
                    value={authPhone}
                    onChange={(e) => setAuthPhone(e.target.value)}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
                  />
                )}

                <input
                  type="password"
                  placeholder="Password *"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
                />

                {authMode === 'register' && (
                  <input
                    type="password"
                    placeholder="Confirm Password *"
                    value={authConfirmPassword}
                    onChange={(e) => setAuthConfirmPassword(e.target.value)}
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
                  />
                )}

                {authMode === 'register' && (
                  <div className="form-consent">
                    <p className="consent-text"><strong>OPTIONAL:</strong><br/>I wish to receive transactional SMS messages from OC MENA Festival regarding orders (not required for purchase). By providing your mobile number and checking this box, you agree to receive transactional SMS messages from OC MENA Festival related to your order. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help. View our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#dc2626', textDecoration: 'underline' }}>Privacy Policy</a> and <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" style={{ color: '#dc2626', textDecoration: 'underline' }}>Terms</a>.</p>
                    <label className="checkbox-label" style={{ paddingLeft: '28px' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={authSmsConsent}
                        onChange={(e) => setAuthSmsConsent(e.target.checked)}
                      />
                      <span className="checkbox-custom"></span>
                      <span>I agree to receive transactional SMS messages from OC MENA Festival regarding orders.</span>
                    </label>
                  </div>
                )}

                {authMode === 'register' && (
                  <p style={{ margin: '0', fontSize: '12px', color: '#888', lineHeight: '1.5', textAlign: 'center' }}>
                    By creating an account you agree to our
                    <br />
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#dc2626', textDecoration: 'underline' }}>Privacy Policy</a>{' '}and{' '}
                    <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" style={{ color: '#dc2626', textDecoration: 'underline' }}>Terms of Use</a>.
                  </p>
                )}

                {authError && (
                  <p style={{ color: '#dc2626', margin: '0', fontSize: '13px', fontWeight: '500' }}>{authError}</p>
                )}
              </div>
              <div className="address-modal-footer" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={authLoading}
                  style={{
                    width: '100%',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '30px',
                    cursor: authLoading ? 'not-allowed' : 'pointer',
                    opacity: authLoading ? 0.7 : 1,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {authLoading ? 'Please wait...' : 'Continue'}
                </button>
                <p style={{ textAlign: 'center', fontSize: '13px', color: '#555', margin: '0' }}>
                  {authMode === 'register' ? (
                    <>Already have an account?{' '}
                      <span
                        onClick={() => { setAuthMode('login'); setAuthError(''); setAuthPassword(''); setAuthConfirmPassword(''); setAuthConfirmEmail(''); }}
                        style={{ color: '#dc2626', cursor: 'pointer', fontWeight: '600' }}
                      >Log in</span>
                    </>
                  ) : (
                    <>Don't have an account?{' '}
                      <span
                        onClick={() => { setAuthMode('register'); setAuthError(''); setAuthPassword(''); setAuthConfirmPassword(''); setAuthConfirmEmail(''); }}
                        style={{ color: '#dc2626', cursor: 'pointer', fontWeight: '600' }}
                      >Sign up</span>
                    </>
                  )}
                </p>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="address-modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="address-modal" onClick={(e) => e.stopPropagation()}>
            <div className="address-modal-header">
              <h3>Enter Your Email</h3>
              <button className="modal-close-btn" onClick={() => setShowEmailModal(false)}>×</button>
            </div>
            <div className="address-modal-content">
              <p>Please enter your email address to receive your tickets and order confirmation.</p>
              <input
                type="email"
                placeholder="your.email@example.com"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleEmailSubmit()}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginTop: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
              {emailError && (
                <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{emailError}</p>
              )}
            </div>
            <div className="address-modal-footer">
              <button 
                className="modal-ok-btn" 
                onClick={handleEmailSubmit}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Continue to Checkout'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Cart;
